import React from 'react'
import { Helmet } from 'react-helmet-async'
import IndiaMap from '../assets/images/data/india.png'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  Truck,
  Warehouse,
  TrainFront,
  Package,
  Plane,
  ChartNoAxesCombined,
  CheckCircle2,
  Phone,
  Ship,
  MapPin,
  Thermometer
} from 'lucide-react'
import FooterPage from './FooterPage'
import WarehousingImage from '../assets/images/data/warehousing.png'
import SurfaceExpressImage from '../assets/images/data/3pl.png'
import ContractLogisticsImage from '../assets/images/data/contract-logistics.png'
import TrainImage from '../assets/images/data/train.png'
import InternationalShippingImage from '../assets/images/data/international-shipping.png'
import AirImage from '../assets/images/data/air.png'
import LastMileImage from '../assets/images/data/new_hero_mobile.png'
import ColdChainImage from '../assets/images/data/cold-chain.png'
import SupplyChainImage from '../assets/images/data/supply-chain.png'

const services = [
  {
    number: '01',
    category: 'Pan-India Express',
    title: 'Surface Express Transportation',
    description:
      'High-speed surface delivery for time-critical regional and long-haul shipments using our modern fleet with guaranteed transit schedules.',
    features: [
      'Dedicated Express Fleet & GPS Tracking',
      'Door-to-door express parcel & FTL/LTL',
      '18,000+ PIN codes covered nationwide'
    ],
    image: SurfaceExpressImage,
    icon: Truck,
    link: '/services/surface-express'
  },
  {
    number: '02',
    category: 'Smart Storage Hubs',
    title: 'Warehousing and Distribution',
    description:
      'Grade-A tech-enabled warehousing, inventory management, pick-pack fulfillment, and localized regional distribution hubs.',
    features: [
      'Real-time WMS inventory tracking',
      'Scalable pallet & rack space flex',
      'Kitting, labeling & value-added services'
    ],
    image: WarehousingImage,
    icon: Warehouse,
    link: '/services/warehousing'
  },
  {
    number: '03',
    category: 'Eco Freight Express',
    title: 'Rail Cargo & Train Express',
    description:
      'Cost-effective, reliable bulk parcel movement leveraging Indian Railways express trains and dedicated freight corridor networks.',
    features: [
      'High-capacity bulk freight haulage',
      'Weather-proof lease SLR/VP rake booking',
      'Significantly lower carbon footprint'
    ],
    image: TrainImage,
    icon: TrainFront,
    link: '/services/rail-cargo'
  },
  {
    number: '04',
    category: 'Tailored Enterprise Logistics',
    title: 'Contract Logistics',
    description:
      'Customized long-term strategic supply chain design, dedicated fleet allocations, and turnkey 3PL operations for corporate clients.',
    features: [
      'Dedicated KPI & SLA management',
      'Customized fleet & warehouse setup',
      'Dedicated key account manager'
    ],
    image: ContractLogisticsImage,
    icon: Package,
    link: '/services/contract-logistics'
  },
  {
    number: '05',
    category: 'Priority Air Cargo',
    title: 'Air Freight Forwarding',
    description:
      'Urgent domestic and international air cargo solutions with IATA partnerships, airport-to-airport, and door-to-door transit options.',
    features: [
      'Express domestic flight dispatch',
      'Global airport-to-door clearance',
      'Chartered air cargo capabilities'
    ],
    image: AirImage,
    icon: Plane,
    link: '/services/air-freight-forwarding'
  },
  {
    number: '06',
    category: 'End-to-End Analytics',
    title: 'Supply Chain Management',
    description:
      'Technology-driven supply chain consulting, route optimization, digital tracking portals, and predictive analytics for maximum efficiency.',
    features: [
      'End-to-end visibility software platform',
      'AI-driven route & payload optimization',
      'Freight audit & vendor consolidation'
    ],
    image: SupplyChainImage,
    icon: ChartNoAxesCombined,
    link: '/services/supply-chain-management'
  },
  {
    number: '07',
    category: 'Global Sea Cargo',
    title: 'International Ocean Freight',
    description:
      'FCL and LCL container shipping across global sea trade lanes, complete with customs clearance, port handling, and documentation.',
    features: [
      'FCL (Full Container) & LCL (Shared)',
      'Customs brokerage & CHA services',
      'Global port-to-port & port-to-door'
    ],
    image: InternationalShippingImage,
    icon: Ship,
    link: '/services/international'
  },
  {
    number: '08',
    category: 'Flexible Final Reach',
    title: 'Last-Mile & Multimodal Delivery',
    description:
      'Seamless combination of Road, Rail, Air, and localized EV fleet networks ensuring flawless final-mile doorstep delivery.',
    features: [
      'Urban & remote tier 2/3/4 final mile reach',
      'Proof of Delivery (e-POD) & OTP validation',
      'Eco-friendly EV city distribution fleets'
    ],
    image: LastMileImage,
    icon: MapPin,
    link: '/services/last-mile-delivery'
  },
  {
    number: '09',
    category: 'Pharma & Food Safety',
    title: 'Cold Chain Transportation',
    description:
      'Temperature-controlled reefer trucks and cold storage for sensitive pharma, diagnostics, dairy, and perishable food products.',
    features: [
      '-25°C to +25°C precision thermal control',
      '24/7 IoT continuous temperature logging',
      'Pharma GDP compliant reefer fleet'
    ],
    image: ColdChainImage,
    icon: Thermometer,
    link: '/services/cold-chain'
  }
]

const stats = [
  { value: '99.4%', label: 'On-Time SLA' },
  { value: '29+', label: 'States Covered' },
  // { value: '9', label: 'Core Services' }
  { value: '18000+', label: 'Pincode Covered' }
]

const Services = () => {
  return (
    <>
      <Helmet>
        <title>Our Services | S Infinite Express</title>
        <meta
          name='description'
          content='Explore a full suite of logistics solutions including PTL, FTL, courier services, warehousing, e-commerce logistics, reverse logistics, and supply chain management at S Infinite Express.'
        />
        <meta
          name='keywords'
          content='PTL services, FTL services, courier solutions, warehousing India, e-commerce logistics, reverse logistics, logistics company India, supply chain solutions, freight forwarding, last mile delivery, logistics for D2C brands'
        />
        <meta name='author' content='S Infinite Express' />
        <meta property='og:title' content='Our Services | S Infinite Express' />
        <meta
          property='og:description'
          content='Discover tailored logistics services including PTL, FTL, warehousing, courier, and more. Trusted by e-commerce and D2C brands across India.'
        />
        <meta property='og:type' content='website' />
        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/services'
        />
        <link rel='canonical' href='https://sinfiniteexpress.com/services' />
      </Helmet>

      <section className='bg-[#211f57] text-white py-20 px-4'>
        <div className='max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center'>
          {/* Left: Text Content */}
          <div>
            <h2 className='text-3xl md:text-5xl font-extrabold leading-snug mb-6'>
              Seamless Logistics <br className='hidden md:block' />
              <span className='text-[#fff44f]'>
                Across Every Corner of India
              </span>
            </h2>

            <div className='bg-red-900/90 border border-yellow-600 text-[#fff44f] rounded-lg py-3 px-5 text-md md:text-base font-medium shadow-md mb-8'>
              Serving every corner of India — fast, reliable, nationwide.
            </div>
            {/* Stats */}
            <div className='space-y-6'>
              <div>
                <p className='text-4xl md:text-5xl font-bold text-[#fff44f]'>
                  29 States
                </p>
                <p className='text-lg text-gray-300 mt-1'>Seamlessly Covered</p>
              </div>
              <div>
                <p className='text-4xl md:text-5xl font-bold text-[#fff44f]'>
                  18,000+
                </p>
                <p className='text-lg text-gray-300 mt-1'>
                  Pincodes Reached Daily
                </p>
              </div>
              <div>
                <p className='text-4xl md:text-5xl font-bold text-[#fff44f]'>
                  100,000+
                </p>
                <p className='text-lg text-gray-300 mt-1'>
                  Shipments Handled Monthly
                </p>
              </div>
            </div>
          </div>

          {/* Right: India Map Image */}
          <div className='flex justify-center'>
            <img
              src={IndiaMap}
              alt='Pan India Coverage Map'
              className='w-full max-w-xl rounded-xl'
            />
          </div>
        </div>
      </section>

      <section className='relative overflow-hidden bg-[#f7f9fc] py-8 sm:py-10 lg:py-12 px-2 sm:px-3 lg:px-5'>
        <div className='max-w-7xl mx-auto'>
          {/* SECTION HEADER */}
          <div className='flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-10'>
            <div className='max-w-2xl'>
              <div className='flex items-center gap-2 text-[#d9232e] text-xs font-extrabold uppercase tracking-[2px] mb-3'>
                <span className='w-7 h-[2px] bg-[#d9232e]' />
                What We Deliver
              </div>

              <h2 className='text-3xl sm:text-4xl lg:text-[40px] leading-tight font-extrabold text-[#101c49]'>
                Our Core{' '}
                <span className='text-[#e1252b]'>Logistics Services</span>
              </h2>

              <p className='text-gray-600 text-sm sm:text-base leading-relaxed mt-3 max-w-xl'>
                End-to-end supply chain expertise powering commercial growth
                across India and globally. Explore our 9 specialized core
                services engineered for speed, safety, and reliability.
              </p>
            </div>

            {/* STATISTICS */}
            <div className='grid grid-cols-3 bg-white border border-slate-200 rounded-2xl shadow-sm px-3 py-4 sm:px-5 sm:py-5 w-full lg:w-[325px] shrink-0'>
              {stats.map((stat, i) => (
                <div
                  key={stat.label}
                  className={`text-center px-2 ${
                    i !== 2 ? 'border-r border-slate-100' : ''
                  }`}
                >
                  <h3
                    className={`text-xl sm:text-2xl font-extrabold ${
                      i === 1 ? 'text-[#e1252b]' : 'text-[#101c49]'
                    }`}
                  >
                    {stat.value}
                  </h3>
                  <p className='text-[10px] sm:text-[11px] text-slate-500 mt-1 whitespace-nowrap'>
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* SERVICE GRID */}
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
            {services.map((service, i) => {
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{
                    duration: 0.5,
                    delay: (i % 3) * 0.08
                  }}
                  className='group bg-white rounded-xl overflow-hidden border border-slate-200 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col'
                >
                  {/* IMAGE HEADER */}
                  <div className='relative h-[166px] overflow-hidden bg-[#101936]'>
                    <img
                      src={service.image}
                      alt={service.title}
                      loading='lazy'
                      decoding='async'
                      className='absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
                    />

                    {/* DARK OVERLAY */}
                    <div className='absolute inset-0 bg-gradient-to-t from-[#080f2d] via-[#101936]/55 to-black/10' />

                    {/* IMAGE CONTENT */}
                    <div className='absolute bottom-0 left-0 right-0 p-4'>
                      <p className='text-[#ffcb05] text-[10px] font-extrabold uppercase tracking-wide mb-1.5'>
                        {service.category}
                      </p>

                      <h3 className='text-white text-base sm:text-[17px] font-extrabold leading-tight'>
                        {service.title}
                      </h3>
                    </div>
                  </div>

                  {/* CARD CONTENT */}
                  <div className='p-3 flex flex-col flex-grow'>
                    <p className='text-[12px] sm:text-[13px] text-slate-600 leading-[1.65] min-h-[60px]'>
                      {service.description}
                    </p>

                    {/* FEATURES */}
                    <div className='space-y-3 mt-2 mb-2 flex-grow'>
                      {service.features.map(feature => (
                        <div key={feature} className='flex items-start gap-2'>
                          <CheckCircle2
                            size={15}
                            className='text-[#10b981] shrink-0 mt-[1px]'
                            fill='#10b981'
                            stroke='white'
                          />
                          <span className='text-[11px] sm:text-xs text-slate-700 leading-relaxed'>
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CARD FOOTER */}
                  <div className='border-t border-slate-100 bg-[#fbfcfe] px-5 py-3 flex items-center justify-end gap-2'>
                    <a
                      href={service.link}
                      className='inline-flex items-center gap-1 whitespace-nowrap bg-[#101c49] text-white text-[10px] font-bold px-4 py-2 rounded-lg hover:bg-[#e1252b] transition-colors'
                    >
                      Know More
                      <ArrowRight size={12} />
                    </a>
                  </div>
                </motion.div>
              )
            })}
          </div>

          {/* BOTTOM CTA BANNER */}
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className='relative overflow-hidden mt-6 bg-[#101c49] rounded-2xl px-6 py-7 sm:px-8 sm:py-8 shadow-xl'
          >
            {/* BACKGROUND DECORATION */}
            <div className='absolute right-0 top-0 w-36 h-full bg-white/5 rounded-l-full translate-x-8' />

            <div className='relative z-10 flex flex-col lg:flex-row items-center justify-between gap-6'>
              {/* LEFT CONTENT */}
              <div className='flex flex-col sm:flex-row items-center text-center sm:text-left gap-4'>
                <div>
                  <p className='text-[#ffcb05] text-[10px] font-extrabold uppercase tracking-wider mb-1'>
                    Complete Logistics Partner
                  </p>

                  <h3 className='text-white text-xl sm:text-2xl font-extrabold'>
                    One Partner. Complete Logistics.
                  </h3>

                  <p className='text-blue-100 text-xs sm:text-sm mt-1 max-w-xl'>
                    Reliable, nationwide transportation, cold-chain, and
                    warehousing solutions tailored for every growing business
                    requirement.
                  </p>
                </div>
              </div>

              {/* RIGHT BUTTONS */}
              <div className='flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto'>
                <a
                  href='/contact-us'
                  className='w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-3 border border-white/30 rounded-lg text-white text-[11px] font-extrabold uppercase hover:bg-white/10 transition-all whitespace-nowrap'
                >
                  <Phone size={14} className='text-[#ffcb05]' />
                  Call Sales Support
                </a>

                <a
                  href='/get-a-quote'
                  className='w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-[#ffcb05] text-[#101c49] rounded-lg text-[11px] font-extrabold uppercase hover:bg-white transition-all whitespace-nowrap'
                >
                  Get A Quote
                  <ArrowRight size={15} />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      <FooterPage />
    </>
  )
}

export default Services
